<template>
  <div ref="seriesRootRef" class="pixso-page product-series" :class="{ 'product-series--motion': motionReady }">
    <main class="product-series__main">
      <section
        class="product-series__hero"
        :class="{ 'product-series__hero--image': heroImage }"
        :aria-labelledby="titleId"
      >
        <img
          v-if="heroImage"
          class="product-series__hero-image"
          :src="heroImage"
          :alt="`${title}${subtitle}`"
        />
        <div
          class="product-series__hero-copy"
          :class="[
            { 'product-series__hero-copy--overlay': heroImage },
            heroCopyClass
          ]"
        >
          <h1
            :id="titleId"
            class="product-series__title"
            :class="heroTitleClass"
          >
            <template v-if="titleLines.length">
              <span
                v-for="line in titleLines"
                :key="line"
                class="product-series__title-line"
              >
                {{ line }}
              </span>
            </template>
            <template v-else>{{ title }}</template>
          </h1>
          <p v-if="subtitle" class="product-series__subtitle">{{ subtitle }}</p>
        </div>
      </section>

      <section class="product-series__products" :aria-label="title">
        <div v-if="rowImages.length" class="product-series__rows">
          <div
            v-for="(rowImage, rowIndex) in rowImages"
            :key="`${title}-row-${rowIndex}`"
            class="product-series__row"
          >
            <img class="product-series__row-image" :src="rowImage" alt="" aria-hidden="true" />
            <component
              :is="getLinkComponent(item.to)"
              v-for="(item, itemIndex) in rowItems[rowIndex]"
              :key="`${title}-row-${rowIndex}-${item.title}`"
              class="product-series__row-hotspot"
              :style="getRowHotspotStyle(rowItems[rowIndex].length, itemIndex)"
              :aria-label="`${item.title} ${item.description || ''} ${moreText}`"
              data-text-reveal="off"
              v-bind="getLinkProps(item.to)"
            >
              <span class="product-series__sr-only">{{ item.title }} {{ moreText }}</span>
            </component>
          </div>
        </div>
        <div
          v-else
          class="product-series__grid"
          :class="{ 'product-series__grid--featured': featured }"
        >
          <component
            :is="getLinkComponent(item.to)"
            v-for="item in items"
            :key="item.title"
            class="product-series__card"
            :class="{ 'product-series__card--featured': item.featured }"
            :aria-label="`${item.title} ${item.description || ''} ${moreText}`"
            :style="{ backgroundImage: `url(${item.image})`, backgroundPosition: item.imagePosition || 'center' }"
            data-text-reveal="off"
            v-bind="getLinkProps(item.to)"
          >
            <span class="product-series__card-copy">
              <span class="product-series__card-title">{{ item.title }}</span>
              <span class="product-series__card-desc">{{ item.description }}</span>
              <span class="product-series__card-link">{{ moreText }} &gt;</span>
            </span>
          </component>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  subtitle: {
    type: String,
    default: ''
  },
  items: {
    type: Array,
    required: true
  },
  heroImage: {
    type: String,
    default: ''
  },
  heroTitleClass: {
    type: [String, Array, Object],
    default: ''
  },
  heroCopyClass: {
    type: [String, Array, Object],
    default: ''
  },
  titleLines: {
    type: Array,
    default: () => []
  },
  rowImages: {
    type: Array,
    default: () => []
  }
})

const { t } = useI18n()
const seriesRootRef = ref(null)
const motionReady = ref(false)
let cardObserver = null
const moreText = computed(() => t('AutomotiveIndex.series.link'))
const titleId = computed(() => `series-${props.title.replace(/\W/g, '').toLowerCase()}`)
const featured = computed(() => props.items.length === 1 || props.items.some((item) => item.featured))

const getLinkComponent = (to) => {
  if (!to) return 'div'
  return 'router-link'
}

const getLinkProps = (to) => {
  if (!to) return {}

  return { to }
}

const rowItems = computed(() => {
  if (!props.rowImages.length) return []

  const rows = []
  let cursor = 0
  props.rowImages.forEach((_, index) => {
    const remainingItems = props.items.length - cursor
    const remainingRows = props.rowImages.length - index
    const take = remainingRows <= 1 ? remainingItems : Math.min(2, remainingItems - (remainingRows - 1))
    rows.push(props.items.slice(cursor, cursor + take))
    cursor += take
  })

  return rows
})

const getRowHotspotStyle = (count, index) => {
  if (count <= 1) {
    return { left: '3.3333%', width: '93.3333%' }
  }

  const gap = 24 / 1920 * 100
  const side = 64 / 1920 * 100
  const width = (100 - side * 2 - gap * (count - 1)) / count
  const left = side + index * (width + gap)

  return {
    left: `${left}%`,
    width: `${width}%`
  }
}

const revealCards = () => {
  const cards = Array.from(seriesRootRef.value?.querySelectorAll('.product-series__card') || [])

  if (!cards.length) {
    return
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  motionReady.value = true
  cardObserver?.disconnect()

  if (prefersReducedMotion) {
    cards.forEach((card) => card.classList.add('product-series__card--visible'))
    return
  }

  cardObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        return
      }

      entry.target.classList.add('product-series__card--visible')
      cardObserver?.unobserve(entry.target)
    })
  }, {
    rootMargin: '50px',
    threshold: 0.01
  })

  cards.forEach((card) => cardObserver.observe(card))
}

onMounted(async () => {
  await nextTick()
  revealCards()
})

watch(() => props.items, async () => {
  await nextTick()
  revealCards()
})

onBeforeUnmount(() => {
  cardObserver?.disconnect()
})
</script>

<style scoped>
.product-series {
  --product-series-hero-copy-max: 1160px;
  --product-series-hero-copy-offset: 58px;
  --product-series-overlay-max: 720px;
  --product-series-overlay-mobile-max: 520px;
  --product-series-overlay-top: 40.1852%;
  --product-series-overlay-mobile-top: 38%;
  --product-series-card-ratio: 884 / 811;
  --product-series-card-featured-ratio: 1792 / 1142;
  --product-series-card-mobile-radius: var(--pixso-radius-lg);
  --product-series-color-card-bg: var(--pixso-color-surface);
  --product-series-hero-fallback-end: #3a3636;
  --product-series-hero-fallback-bg:
    linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(58, 54, 54, 0.3) 100%),
    linear-gradient(180deg, var(--pixso-color-bg) 0%, var(--product-series-hero-fallback-end) 100%);
  --product-series-card-overlay: linear-gradient(180deg, rgba(0, 0, 0, 0) 46%, rgba(0, 0, 0, 0.66) 100%);
  --product-series-card-featured-overlay: linear-gradient(180deg, rgba(0, 0, 0, 0) 54%, rgba(0, 0, 0, 0.72) 100%);
  --product-series-row-gap: var(--pixso-fs-64);
  --product-series-row-gap-tablet: var(--pixso-fs-36);
  --product-series-row-gap-mobile: var(--pixso-fs-28);
  --product-series-card-padding-x: var(--pixso-fs-32);
  --product-series-card-padding-bottom: 44px;
  --product-series-card-hover-y: -4px;
  --product-series-tablet-products-padding-bottom: calc(var(--pixso-section-gap-xl) - var(--pixso-fs-20));
}

.product-series__main {
  width: 100%;
  background: var(--pixso-color-bg);
}

.product-series__hero {
  width: 100%;
  height: var(--pixso-hero-height);
  min-height: var(--pixso-hero-min);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--product-series-hero-fallback-bg);
}

.product-series__hero--image {
  display: block;
  background: var(--pixso-color-bg);
}

.product-series__hero-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.product-series__hero-copy {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--product-series-hero-copy-max));
  margin-top: var(--product-series-hero-copy-offset);
  text-align: center;
}

.product-series__hero-copy--overlay {
  position: absolute;
  z-index: 1;
  top: var(--product-series-overlay-top);
  left: 50%;
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--product-series-overlay-max));
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--pixso-gap-sm);
  transform: translateX(-50%);
  text-align: center;
  pointer-events: none;
}

.product-series__title {
  margin: 0 0 var(--pixso-gap-sm);
  color: var(--pixso-color-text);
  font-family: var(--pixso-font-en);
  font-size: clamp(var(--pixso-fs-56), 5vw, var(--pixso-fs-96));
  font-weight: bold;
  line-height: clamp(var(--pixso-lh-56), 6vw, var(--pixso-lh-96));
  letter-spacing: 0;
}

.product-series__subtitle {
  margin: 0;
  color: var(--pixso-color-text);
  font-size: clamp(var(--pixso-fs-48), 3.75vw, var(--pixso-fs-72));
  font-weight: 700;
  line-height: clamp(var(--pixso-lh-48), 4.48vw, var(--pixso-lh-72));
  letter-spacing: 0;
}

.product-series__hero-copy--overlay .product-series__title {
  margin: 0;
  font-size: var(--pixso-fs-96);
  font-weight: bold;
  line-height: var(--pixso-lh-96);
  max-width: 100%;
  overflow-wrap: normal;
  word-break: keep-all;
  text-wrap: balance;
}

.product-series__hero-copy--overlay.product-series__hero-copy--wide-title {
  width: min(calc(100% - var(--pixso-page-padding) * 2), 1040px);
}

.product-series__hero-copy--overlay .product-series__title.product-series__hero-title--balanced-two-line {
  font-size: var(--pixso-fs-72);
  line-height: var(--pixso-lh-72);
}

.product-series__title-line {
  display: block;
}

.product-series__hero-copy--overlay .product-series__title.product-series__hero-title--balanced-two-line .product-series__title-line {
  white-space: nowrap;
}

.product-series__hero-copy--overlay .product-series__subtitle {
  margin: 0;
  font-size: var(--pixso-fs-72);
  font-weight: 700;
  line-height: var(--pixso-lh-72);
  max-width: 100%;
  overflow-wrap: normal;
  word-break: keep-all;
}

.product-series__hero-copy--overlay .product-series__title.product-series__hero-title--compact {
  max-width: 780px;
  font-size: clamp(var(--pixso-fs-56), 4.8vw, var(--pixso-fs-80, 80px));
  line-height: clamp(var(--pixso-lh-56), 5.2vw, var(--pixso-lh-80, 96px));
}

.product-series__products {
  width: 100%;
  max-width: var(--pixso-page-max);
  margin: 0 auto;
  padding: var(--pixso-fs-96) var(--pixso-page-padding) var(--pixso-section-gap-xl);
}

.product-series__rows {
  width: calc(100% + var(--pixso-page-padding) * 2);
  margin-left: calc(var(--pixso-page-padding) * -1);
  display: flex;
  flex-direction: column;
  gap: var(--product-series-row-gap);
}

.product-series__row {
  width: 100%;
  position: relative;
}

.product-series__row-image {
  display: block;
  width: 100%;
  height: auto;
}

.product-series__row-hotspot {
  position: absolute;
  top: 0;
  bottom: 0;
  color: transparent;
  text-decoration: none;
}

.product-series__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.product-series__grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--product-series-row-gap) var(--pixso-gap-md);
}

.product-series__grid--featured {
  grid-template-columns: 1fr;
}

.product-series__card {
  width: 100%;
  aspect-ratio: var(--product-series-card-ratio);
  border-radius: var(--pixso-radius-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding: 0 var(--product-series-card-padding-x) var(--product-series-card-padding-bottom);
  position: relative;
  color: var(--pixso-color-text);
  text-decoration: none;
  background-color: var(--product-series-color-card-bg);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  isolation: isolate;
  transition:
    opacity 0.48s ease,
    transform 0.48s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.48s cubic-bezier(0.22, 1, 0.36, 1);
}

.product-series--motion .product-series__card {
  opacity: 0;
  transform: translate3d(0, 36px, 0);
  filter: blur(12px);
}

.product-series--motion .product-series__card--visible {
  opacity: 1;
  transform: translate3d(0, 0, 0);
  filter: blur(0);
}

.product-series__card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--product-series-card-overlay);
  pointer-events: none;
  z-index: 0;
}

.product-series__card:hover {
  transform: translateY(var(--product-series-card-hover-y));
  filter: brightness(1.06);
}

.product-series--motion .product-series__card--visible:hover {
  transform: translateY(var(--product-series-card-hover-y));
  filter: brightness(1.06);
}

.product-series__card--featured {
  aspect-ratio: var(--product-series-card-featured-ratio);
  padding: 0 var(--pixso-gap-lg) var(--pixso-gap-lg);
}

.product-series__card--featured::before {
  background: var(--product-series-card-featured-overlay);
}

.product-series__card-copy {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  z-index: 1;
}

.product-series__card-title {
  color: var(--pixso-color-text);
  font-size: clamp(var(--pixso-fs-24), 1.875vw, var(--pixso-fs-36));
  font-weight: 400;
  line-height: clamp(var(--pixso-lh-24), 2.45vw, var(--pixso-lh-36));
}

.product-series__card-desc {
  color: var(--pixso-color-text);
  font-size: clamp(var(--pixso-fs-16), 1.25vw, var(--pixso-fs-24));
  font-weight: 400;
  line-height: clamp(var(--pixso-lh-16), 1.77vw, var(--pixso-lh-24));
}

.product-series__card-link {
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-16);
  font-weight: 700;
  line-height: var(--pixso-lh-16);
  text-decoration: underline;
  text-underline-offset: 3px;
}

@media (prefers-reduced-motion: reduce) {
  .product-series--motion .product-series__card {
    opacity: 1;
    transform: none;
    filter: none;
  }
}

@media (max-width: 1200px) {
  .product-series__products {
    padding-top: var(--pixso-fs-72);
    padding-bottom: var(--product-series-tablet-products-padding-bottom);
  }

  .product-series__grid {
    gap: var(--product-series-row-gap-tablet) var(--pixso-gap-md);
  }

  .product-series__rows {
    gap: var(--product-series-row-gap-tablet);
  }
}

@media (max-width: 768px) {
  .product-series__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
  }

  .product-series__hero-copy {
    margin-top: var(--pixso-gap-md);
  }

  .product-series__hero-copy--overlay {
    top: var(--product-series-overlay-mobile-top);
    width: min(calc(100% - var(--pixso-page-padding) * 2), var(--product-series-overlay-mobile-max));
    margin: 0;
  }

  .product-series__title {
    font-size: var(--pixso-fs-48);
    line-height: var(--pixso-lh-48);
  }

  .product-series__subtitle {
    font-size: var(--pixso-fs-32);
    line-height: var(--pixso-lh-32);
  }

  .product-series__hero-copy--overlay .product-series__title {
    font-size: var(--pixso-fs-48);
    line-height: var(--pixso-lh-48);
  }

  .product-series__hero-copy--overlay .product-series__subtitle {
    font-size: var(--pixso-fs-32);
    line-height: var(--pixso-lh-32);
  }

  .product-series__hero-copy--overlay .product-series__title.product-series__hero-title--compact {
    max-width: 100%;
    font-size: var(--pixso-fs-44, 44px);
    line-height: var(--pixso-lh-44, 54px);
  }

  .product-series__hero-copy--overlay .product-series__title.product-series__hero-title--balanced-two-line {
    font-size: var(--pixso-fs-44, 44px);
    line-height: var(--pixso-lh-44, 54px);
  }

  .product-series__products {
    padding-top: var(--pixso-gap-lg);
    padding-bottom: var(--pixso-section-gap-xl);
  }

  .product-series__grid {
    grid-template-columns: 1fr;
    gap: var(--product-series-row-gap-mobile);
  }

  .product-series__rows {
    gap: var(--product-series-row-gap-mobile);
  }

  .product-series__card {
    border-radius: var(--product-series-card-mobile-radius);
  }

  .product-series__card--featured {
    aspect-ratio: var(--product-series-card-ratio);
    padding: 0 var(--pixso-fs-20) var(--pixso-lh-24);
  }

  .product-series__card-title {
    font-size: var(--pixso-fs-24);
    line-height: var(--pixso-lh-24);
  }

  .product-series__card-desc {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }
}

@media (max-width: 420px) {
  .product-series__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
  }

  .product-series__title {
    font-size: var(--pixso-fs-36);
    line-height: var(--pixso-lh-36);
  }

  .product-series__subtitle {
    font-size: var(--pixso-fs-24);
    line-height: var(--pixso-lh-24);
  }

  .product-series__hero-copy--overlay .product-series__title {
    font-size: var(--pixso-fs-36);
    line-height: var(--pixso-lh-36);
  }

  .product-series__hero-copy--overlay .product-series__subtitle {
    font-size: var(--pixso-fs-24);
    line-height: var(--pixso-lh-24);
  }

  .product-series__hero-copy--overlay .product-series__title.product-series__hero-title--compact {
    font-size: var(--pixso-fs-36);
    line-height: var(--pixso-lh-36);
  }

  .product-series__hero-copy--overlay .product-series__title.product-series__hero-title--balanced-two-line {
    font-size: var(--pixso-fs-36);
    line-height: var(--pixso-lh-36);
  }

  .product-series__hero-copy--overlay .product-series__title.product-series__hero-title--balanced-two-line .product-series__title-line {
    white-space: normal;
  }
}
</style>
